import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";
import Loader from "js/Component/Loader";
import DeleteModal from "js/Component/DeleteModal";
import ModalWrapper from "js/Component/ModalWrapper";
import AddEditSubQuestionModal from "./AddEditSubQuestionModal";
import AddEditFinishingPageModal from "./AddEditFinishingPageModal";
import AddEditEmailAction from "./AddEditEmailAction";
import {
    createFormFieldWithSub,
    updateFormField,
    updateFormFieldOptionImage, RemoveFormFieldOptionImage,
    deleteFormField,
    duplicateFormFieldOption,
    fetchAllFormFields,
} from "js/Api/forms";
import {
    FREE_QUE_TYPE,
    MULTIPLE_QUE_TYPE,
    SINGLE_QUE_TYPE,
    QuestionTypes,
} from "js/Constants/forms";
import "../../../../css/questionModal.css";
import "../../../../css/customSelect.css";
import PreviewQuestionDetails from "./PreviewQuestionDetails"

const CustomSelect = ({
    questionType,
    setQuestionType,
    questionTypesOptions,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedOption = questionTypesOptions.find(
        (q) => q.value === questionType
    ) ||
        questionTypesOptions[0] || {
        value: "",
        label: "Select type",
        icon: "fa fa-question-circle",
    };

    const handleSelect = (option) => {
        if (!disabled) {
            setQuestionType(option.value);
            setIsOpen(false);
        }
    };

    const handleDisplayClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="custom-select-container">
            <div
                className={`custom-select-display ${disabled ? "disabled" : ""}`}
                onClick={handleDisplayClick}
                style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <i className={selectedOption?.icon} style={{ marginRight: "8px" }}></i>
                <span style={{ flex: 1 }}>{selectedOption.label}</span>
                <i
                    className="fa fa-caret-down"
                    style={{
                        marginLeft: "8px",
                        cursor: disabled ? "not-allowed" : "pointer",
                    }}
                ></i>
            </div>

            {isOpen && !disabled && (
                <div className="custom-select-dropdown">
                    {questionTypesOptions.map((option) => (
                        <div
                            key={option.value}
                            className="custom-select-option"
                            onClick={() => handleSelect(option)}
                        >
                            <i className={option?.icon} style={{ marginRight: "8px" }}></i>
                            <span>{option.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ColorDropdown = ({ currentColor, onColorChange, onClose }) => {
    const colors = [
        "#FF6B6B",
        "#4ECDC4",
        "#45B7D1",
        "#96CEB4",
        "#FFEAA7",
        "#DDA0DD",
        "#98D8C8",
        "#F7DC6F",
        "#BB8FCE",
        "#85C1E9",
        "#FF9FF3",
        "#54A0FF",
        "#5F27CD",
        "#00D2D3",
        "#FF9F43",
        "#FFA502",
        "#FF6348",
        "#2ED573",
        "#3742FA",
        "#A4B0BE",
        "#2F3542",
        "#40407A",
        "#706FD3",
        "#F8B500",
        "#F97F51",
        "#6C5CE7",
        "#A29BFE",
        "#FD79A8",
        "#00B894",
        "#00CEC9",
    ];

    return (
        <div
            style={{
                position: "absolute",
                top: "100%",
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 1000,
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "4px",
                minWidth: "140px",
                maxHeight: "120px",
                overflowY: "auto",
            }}
        >
            {colors.map((color) => (
                <div
                    key={color}
                    onClick={() => onColorChange(color)}
                    style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: color,
                        border:
                            currentColor === color ? "2px solid #333" : "1px solid #ddd",
                        borderRadius: "3px",
                        cursor: "pointer",
                    }}
                />
            ))}
            {currentColor && (
                <div
                    onClick={() => onColorChange(null)}
                    style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "3px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                    }}
                >
                    ✕
                </div>
            )}
        </div>
    );
};

const SortableOptionItem = ({
    option,
    index,
    questionType,
    formType,
    showRemoveButton,
    onInputChange,
    onImageChange,
    onColorChange,
    onRemove,
    onAddSubQuestion,
    onEditSubQuestion,
    onAddFinishingPage,
    onEditFinishingPage,
    onAddEmailAction,
    onDuplicate,
    canDuplicate,
    subQuestionList,
    subQuestionDrafts,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({
            id: option.id,
        });

    const [showColorDropdown, setShowColorDropdown] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: true });
    const fileInputRef = useRef();
    const moreButtonRef = useRef();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: "16px",
    };

    const hasSubQuestion =
        subQuestionList?.some((subQ) => subQ.refFieldOption === option.id) ||
        subQuestionDrafts?.[option.id];

    const hasFinishingPage = option.finishingDesc && option.finishingDesc.trim();
    const hasCusotmAction = option.customAction;

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png"];
            const maxSize = 1 * 1024 * 1024; // 1MB

            if (!allowedTypes.includes(file.type)) {
                toast.error("Only PNG and JPG files are allowed.");
                return;
            }

            if (file.size > maxSize) {
                toast.error("Image size must be less than 1 MB.");
                return;
            }

            onImageChange(option.id, file);
        }
    };

    const getImagePreview = () => {
        if (option.image?.name) {
            return URL.createObjectURL(option.image);
        }
        return option.image || null;
    };

    const calculateMenuPosition = () => {
        if (moreButtonRef.current) {
            const buttonRect = moreButtonRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const menuHeight = 200;
            const spaceBelow = viewportHeight - buttonRect.bottom;

            setMenuPosition({ top: spaceBelow > menuHeight });
        }
    };

    const handleMoreMenuToggle = () => {
        if (!showMoreMenu) {
            calculateMenuPosition();
        }
        setShowMoreMenu(!showMoreMenu);
    };

    const handleDuplicate = async () => {
        setShowMoreMenu(false);
        if (onDuplicate) {
            onDuplicate(option.id);
        }
    };

    const handleSubQuestionAction = () => {
        setShowMoreMenu(false);
        if (hasSubQuestion) {
            onEditSubQuestion(option.id);
        } else {
            onAddSubQuestion(option.id);
        }
    };

    const handleEmailAction = () => {
        setShowMoreMenu(false);
        onAddEmailAction(option.id);
    };

    const handleFinishingPageAction = () => {
        setShowMoreMenu(false);

        if (hasFinishingPage) {
            onEditFinishingPage(option.id);
        } else {
            if (hasSubQuestion) {
                if (
                    window.confirm(
                        "Adding a finishing page will remove the sub-question. Continue?"
                    )
                ) {
                    onAddFinishingPage(option.id);
                }
            } else {
                onAddFinishingPage(option.id);
            }
        }
    };

    const handleImagePick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
        }
    };

    const handleColorPickerToggle = () => {
        setShowColorDropdown(!showColorDropdown);
    };

    const handleColorSave = (color) => {
        if (onColorChange) {
            onColorChange(option.id, color);
        }
        setShowColorDropdown(false);
    };

    const handleColorDropdownClose = () => {
        setShowColorDropdown(false);
    };

    return (
        <div {...listeners} ref={setNodeRef} style={style} {...attributes}>
            {questionType === "multiple" ? (
                <i className="fa fa-square-o" aria-hidden="true" />
            ) : (
                <i className="fa fa-circle-o" aria-hidden="true" />
            )}

            <input
                type="text"
                value={option.text || ""}
                onChange={(e) => onInputChange(option.id, e.target.value)}
                placeholder={`Option ${index + 1}`}
                style={{
                    padding: "5px",
                }}
            />

            <div style={{ flex: 1 }}></div>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                {(formType === "general" ||
                    formType === "checklist" ||
                    formType === "single-question") && (
                        <>
                            {hasFinishingPage ? (
                                <i
                                    className="fa fa-flag-checkered"
                                    style={{
                                        fontSize: "16px",
                                        color: "#4caf50",
                                    }}
                                    title="Has finishing page"
                                />
                            ) : hasSubQuestion ? (
                                <i
                                    className="fa fa-sitemap"
                                    style={{
                                        fontSize: "16px",
                                        color: "#4caf50",
                                    }}
                                    title="Has sub-question"
                                />
                            ) : null}

                            {hasCusotmAction && <>
                                <i
                                    className="fa fa-envelope-o"
                                    style={{
                                        fontSize: "16px",
                                        color: "#4caf50",
                                    }}
                                    title="Has Email Actions"
                                />
                            </>}
                        </>
                    )}

                {formType === "single-question" && (
                    <div style={{ position: "relative" }}>
                        {option.bgColorCode ? (
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: option.bgColorCode,
                                    border: "1px solid #ddd",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                }}
                                onClick={handleColorPickerToggle}
                                title={`Current color: ${option.bgColorCode}`}
                            />
                        ) : (
                            <i
                                className="fa fa-paint-brush"
                                style={{
                                    fontSize: "16px",
                                    color: "#999",
                                    cursor: "pointer",
                                }}
                                onClick={handleColorPickerToggle}
                                title="Set color"
                            />
                        )}

                        {showColorDropdown && (
                            <>
                                <div
                                    style={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 999,
                                    }}
                                    onClick={handleColorDropdownClose}
                                />
                                <ColorDropdown
                                    currentColor={option.bgColorCode}
                                    onColorChange={handleColorSave}
                                    onClose={handleColorDropdownClose}
                                />
                            </>
                        )}
                    </div>
                )}

                <div
                    style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={handleImagePick}
                    title={option.image ? "Change image" : "Add image"}
                >
                    {getImagePreview() ? (
                        <img
                            src={getImagePreview()}
                            alt="Option preview"
                            style={{
                                width: "16px",
                                height: "16px",
                                objectFit: "cover",
                                borderRadius: "2px",
                                border: "1px solid #ddd",
                            }}
                        />
                    ) : (
                        <i
                            className="fa fa-image"
                            style={{
                                fontSize: "16px",
                                color: "#999",
                            }}
                        />
                    )}
                </div>

                <div style={{ position: "relative" }}>
                    <i
                        ref={moreButtonRef}
                        className="fa fa-ellipsis-v"
                        style={{
                            fontSize: "16px",
                            color: "#999",
                            cursor: "pointer",
                            padding: "5px"
                        }}
                        onClick={handleMoreMenuToggle}
                        title="More options"
                    />

                    {showMoreMenu && (
                        <>
                            <div
                                style={{
                                    position: "fixed",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 9998,
                                }}
                                onClick={() => setShowMoreMenu(false)}
                            />
                            <div
                                style={{
                                    position: "fixed",
                                    ...(menuPosition.top
                                        ? {
                                            top:
                                                moreButtonRef.current?.getBoundingClientRect()
                                                    .bottom +
                                                4 +
                                                "px",
                                        }
                                        : {
                                            bottom:
                                                window.innerHeight -
                                                moreButtonRef.current?.getBoundingClientRect().top +
                                                4 +
                                                "px",
                                        }),
                                    left:
                                        moreButtonRef.current?.getBoundingClientRect().right -
                                        180 +
                                        "px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "4px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    zIndex: 9999,
                                    minWidth: "180px",
                                }}
                            >
                                {canDuplicate && (
                                    <div
                                        onClick={handleDuplicate}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor = "#fff")
                                        }
                                    >
                                        <i
                                            className="fa fa-copy"
                                            style={{ marginRight: "8px", color: "#666" }}
                                        />
                                        Duplicate
                                    </div>
                                )}

                                {showRemoveButton && (
                                    <div
                                        onClick={() => {
                                            onRemove(option.id);
                                            setShowMoreMenu(false);
                                        }}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor = "#fff")
                                        }
                                    >
                                        <i
                                            className="fa fa-trash"
                                            style={{ marginRight: "8px", color: "#666" }}
                                        />
                                        Remove
                                    </div>
                                )}

                                <div
                                    onClick={handleSubQuestionAction}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor = "#fff")
                                    }
                                >
                                    <i
                                        className="fa fa-sitemap"
                                        style={{ marginRight: "8px", color: "#666" }}
                                    />
                                    {hasSubQuestion ? "Edit sub-question" : "Add sub-question"}
                                </div>

                                {formType !== "checklist" && (
                                    <div
                                        onClick={handleFinishingPageAction}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor = "#fff")
                                        }
                                    >
                                        <i
                                            className="fa fa-flag-checkered"
                                            style={{ marginRight: "8px", color: "#666" }}
                                        />
                                        {hasFinishingPage
                                            ? "Edit finishing page"
                                            : "Add finishing page"}
                                    </div>
                                )}

                                <div
                                    onClick={handleEmailAction}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor = "#fff")
                                    }
                                >
                                    <i
                                        className="fa fa-envelope-o"
                                        style={{ marginRight: "8px", color: "#666" }}
                                    />
                                    {hasCusotmAction ? "Edit email action" : "Add email action"}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
            />
        </div>
    );
};

const AddEditQuestionModal = (props) => {
    const {
        isVisible,
        onClose,
        formId,
        formType = "general",
        questionList,
        refetchQuestionsList,
        editAbleQuestionObj,
        setEditAbleQuestionObj,
        subQuestionList,
        customEmailText,
    } = props;

    const [question, setQuestion] = useState("");
    const [questionType, setQuestionType] = useState(MULTIPLE_QUE_TYPE.id);
    const [isLoading, setIsLoading] = useState(false);
    const [isMandatory, setIsMandatory] = useState(false);
    const [isDeleteFormModal, setIsDeleteFormModal] = useState(false);
    const [options, setOptions] = useState([]);

    const [isSubQuestionModalVisible, setIsSubQuestionModalVisible] =
        useState(false);
    const [selectedOptionForSubQuestion, setSelectedOptionForSubQuestion] =
        useState(null);
    const [isFinishingPageModalVisible, setIsFinishingPageModalVisible] =
        useState(false);
    const [selectedOptionForFinishingPage, setSelectedOptionForFinishingPage] =
        useState(null);
    const [selectedOptionForEmailAction, setselectedOptionForEmailAction] =
        useState(null);

    const [subQuestionDrafts, setSubQuestionDrafts] = useState({});
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const createDefaultOptions = useCallback(() => {
        return [
            {
                id: `new-${Date.now()}-1`,
                text: "",
                image: null,
                bgColorCode: null,
                finishingDesc: "",
                finishingImage: null,
                customAction: "",
            },
            {
                id: `new-${Date.now()}-2`,
                text: "",
                image: null,
                bgColorCode: null,
                finishingDesc: "",
                finishingImage: null,
                customAction: "",
            },
        ];
    }, []);

    const isCreateMode = !editAbleQuestionObj?.id;
    const subModalStandalone = !isCreateMode;

    useEffect(() => {
        if (editAbleQuestionObj?.id) {
            setQuestion(editAbleQuestionObj.fieldName);
            setQuestionType(editAbleQuestionObj.fieldType);

            setIsMandatory(
                formType === "single-question" || formType === "checklist"
                    ? true
                    : editAbleQuestionObj.mandatory || false
            );

            if (editAbleQuestionObj.fieldOptions?.length) {
                setOptions(
                    editAbleQuestionObj.fieldOptions.map((o) => ({
                        id: o.id,
                        text: o.fieldOption,
                        image: o.imageURL ?? null,
                        bgColorCode: o.bgColorCode ?? null,
                        finishingDesc: o.finishingDesc ?? "",
                        finishingImage: o.finishingImageURL ?? null,
                        customAction: o.customAction ?? null,
                    }))
                );
            } else setOptions(createDefaultOptions());
        } else {
            setQuestionType(
                formType === "checklist" || formType === "single-question"
                    ? SINGLE_QUE_TYPE.id
                    : MULTIPLE_QUE_TYPE.id
            );
            setIsMandatory(
                formType === "single-question" || formType === "checklist"
            );
            setOptions(createDefaultOptions());
        }
    }, [editAbleQuestionObj, formType, createDefaultOptions]);

    const getAvailableQuestionTypes = () => {
        if (formType === "checklist") {
            return QuestionTypes.filter((type) => type.value === SINGLE_QUE_TYPE.id);
        } else if (formType === "single-question") {
            return QuestionTypes.filter((type) => type.value === SINGLE_QUE_TYPE.id);
        }
        return QuestionTypes;
    };

    const isFormValid = useCallback(() => {
        if (!question || question.trim().length < 2) return false;
        if (questionType === FREE_QUE_TYPE.id) return true;

        if (formType === "single-question") {
            return (
                options.length >= 2 &&
                options.length <= 4 &&
                options.every((o) => o.text && o.text.trim())
            );
        }
        return options.length >= 2 && options.every((o) => o.text && o.text.trim());
    }, [question, questionType, options, formType]);

    const handleOptionInputChange = (optionId, value) => {
        setOptions((prev) =>
            prev.map((opt) => (opt.id === optionId ? { ...opt, text: value } : opt))
        );
    };

    const handleOptionImageChange = (optionId, file) => {
        setOptions((prev) =>
            prev.map((opt) => (opt.id === optionId ? { ...opt, image: file } : opt))
        );
    };

    const handleOptionColorChange = (optionId, color) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId ? { ...opt, bgColorCode: color } : opt
            )
        );
    };

    const handleRemoveOption = (optionId) => {
        if (options.length <= 2) {
            toast.error("At least 2 options are required");
            return;
        }
        setOptions((prev) => {
            const filtered = prev.filter((opt) => opt.id !== optionId);

            return filtered;
        });

        if (subQuestionDrafts[optionId]) {
            setSubQuestionDrafts((prev) => {
                const updated = { ...prev };
                delete updated[optionId];
                return updated;
            });
        }
    };

    const handleAddOption = () => {
        if (formType === "single-question" && options.length >= 4) {
            toast.error("Single-question forms can have maximum 4 options");
            return;
        }

        const newOption = {
            id: `new-${Date.now()}-${Math.random()}`,
            text: "",
            image: null,
            bgColorCode: null,
            finishingDesc: "",
            finishingImage: null,
            customAction: null,
        };

        setOptions((prev) => [...prev, newOption]);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setOptions((prev) => {
            const oldIndex = prev.findIndex((opt) => opt.id === active.id);
            const newIndex = prev.findIndex((opt) => opt.id === over.id);

            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleDuplicateOption = async (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        if (!option) return;

        if (!optionId.startsWith("new-") && editAbleQuestionObj?.id) {
            try {
                toast.loading("Duplicating option...");
                const response = await duplicateFormFieldOption(optionId);

                if (response?.success) {
                    toast.dismiss();
                    toast.success("Option duplicated successfully");
                    refetchQuestionsList();

                    try {
                        const updatedResponse = await fetchAllFormFields(formId);
                        if (updatedResponse?.success && updatedResponse?.data) {
                            const updatedQuestion = updatedResponse.data.find(
                                (field) => field.id === editAbleQuestionObj.id
                            );

                            if (updatedQuestion && updatedQuestion.fieldOptions) {
                                const updatedOptions = updatedQuestion.fieldOptions
                                    .sort((a, b) => a.optionOrder - b.optionOrder)
                                    .map((option) => ({
                                        id: option.id,
                                        text: option.fieldOption,
                                        image: option.imageURL ?? null,
                                        bgColorCode: option.bgColorCode ?? null,
                                        finishingDesc: option.finishingDesc ?? "",
                                        finishingImage: option.finishingImageURL ?? null,
                                        customAction: option.customAction ?? null,
                                    }));

                                setOptions(updatedOptions);

                                setEditAbleQuestionObj(updatedQuestion);
                            }
                        }
                    } catch (refreshError) {
                        console.error("Error refreshing question data:", refreshError);
                    }
                } else {
                    toast.dismiss();
                    toast.error(response?.error || "Failed to duplicate option");
                }
            } catch (error) {
                toast.dismiss();
                toast.error("Failed to duplicate option");
                console.error("Error duplicating option:", error);
            }
        } else {
            const duplicatedOption = {
                ...option,
                id: `new-${Date.now()}-${Math.random()}`,
                text: option.text + (option.text ? " " : ""),

                image: option.image ? option.image : null,
                finishingImage: option.finishingImage ? option.finishingImage : null,

                bgColorCode: option.bgColorCode,
                finishingDesc: option.finishingDesc,
                customAction: option.customAction,
            };

            if (subQuestionDrafts[optionId]) {
                const originalSubQuestionDraft = subQuestionDrafts[optionId];
                const duplicatedSubQuestionDraft = {
                    ...originalSubQuestionDraft,
                    fieldName: originalSubQuestionDraft.fieldName,
                    fieldOptions:
                        originalSubQuestionDraft.fieldOptions?.map((subOpt, index) => ({
                            ...subOpt,
                            id: `draft-${Date.now()}-${index}`,
                            fieldOption: subOpt.fieldOption,

                            image: subOpt.image ? subOpt.image : null,
                            finishingImage: subOpt.finishingImage
                                ? subOpt.finishingImage
                                : null,
                        })) || [],
                };

                setSubQuestionDrafts((prev) => ({
                    ...prev,
                    [duplicatedOption.id]: duplicatedSubQuestionDraft,
                }));
            }

            const optionIndex = options.findIndex((opt) => opt.id === optionId);
            setOptions((prev) => [
                ...prev.slice(0, optionIndex + 1),
                duplicatedOption,
                ...prev.slice(optionIndex + 1),
            ]);

            toast.success("Option duplicated locally");
        }
    };

    const handleAddSubQuestion = (optionId) => {
        setSelectedOptionForSubQuestion(optionId);
        setIsSubQuestionModalVisible(true);
    };

    const [isEmailActionVisible, setisEmailActionVisible] = useState(false);
    const handleCloseEmailAction = () => {
        setisEmailActionVisible(false);
    };

    const handleAddEmailAction = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setselectedOptionForEmailAction(option);
        setisEmailActionVisible(true);
    };

    const onEmailSubmitAction = (optionId, EmailData) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId
                    ? {
                        ...opt,
                        customAction: JSON.stringify(EmailData),
                    }
                    : opt
            )
        );
    };

    const handleEditSubQuestion = (optionId) => {
        setSelectedOptionForSubQuestion(optionId);
        setIsSubQuestionModalVisible(true);
    };

    const handleSubQuestionClose = () => {
        setIsSubQuestionModalVisible(false);
        setSelectedOptionForSubQuestion(null);
    };

    const handleAddFinishingPage = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setSelectedOptionForFinishingPage(option);
        setIsFinishingPageModalVisible(true);
    };

    const handleEditFinishingPage = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setSelectedOptionForFinishingPage(option);
        setIsFinishingPageModalVisible(true);
    };

    const handleFinishingPageSave = (optionId, finishingData) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId
                    ? {
                        ...opt,
                        finishingDesc: finishingData.finishingDesc,
                        finishingImage: finishingData.finishingImage,
                    }
                    : opt
            )
        );
    };

    const handleFinishingPageClose = () => {
        setIsFinishingPageModalVisible(false);
        setSelectedOptionForFinishingPage(null);
    };

    const buildPayload = () => {
        const userId = JSON.parse(localStorage.getItem("userInfo") ?? "{}").userId;
        return {
            formId,
            fieldName: question.trim(),
            fieldType: questionType,
            fieldOrder: (questionList?.length ?? 0) + 1,
            userID: userId,
            mandatory: isMandatory,
            fieldOptions: options
                .filter((o) => o.text.trim())
                .map((o, idx) => {
                    const opt = {
                        fieldOption: o.text.trim(),
                        optionOrder: idx + 1,
                        bgColorCode: o.bgColorCode ?? null,
                        finishingDesc: o.finishingDesc?.trim() || null,
                        customAction: o.customAction || null,
                    };

                    if (subQuestionDrafts[o.id]) {
                        opt.subField = {
                            fieldName: subQuestionDrafts[o.id].fieldName,
                            fieldType: subQuestionDrafts[o.id].fieldType,
                            formId,
                            fieldOrder: 1,
                            userID: userId,
                            mandatory: subQuestionDrafts[o.id].mandatory || false,
                            fieldOptions:
                                subQuestionDrafts[o.id].fieldOptions?.map((subOpt) => ({
                                    fieldOption: subOpt.fieldOption,
                                    optionOrder: subOpt.optionOrder,
                                    bgColorCode: subOpt.bgColorCode ?? null,
                                    finishingDesc: subOpt.finishingDesc ?? null,
                                    customAction: subOpt.customAction || null,
                                })) || [],
                        };
                    }
                    return opt;
                }),
        };
    };

    const buildImageUploadMap = (responseData) => {
        const imageUploads = [];
        const fieldOptionsResponse = responseData?.fieldOptions || [];

        options.forEach((option, optionIndex) => {
            const matchingMainOption = fieldOptionsResponse.find(
                (resOpt) => resOpt.optionOrder === optionIndex + 1
            );

            if (matchingMainOption) {
                if (option.image && option.image.name) {
                    imageUploads.push({
                        optionId: matchingMainOption.id,
                        file: option.image,
                        isFinishing: false,
                        type: "main",
                        description: `Main option ${optionIndex + 1} image`,
                    });
                }

                if (option.finishingImage && option.finishingImage.name) {
                    imageUploads.push({
                        optionId: matchingMainOption.id,
                        file: option.finishingImage,
                        isFinishing: true,
                        type: "main",
                        description: `Main option ${optionIndex + 1} finishing image`,
                    });
                }

                if (subQuestionDrafts[option.id] && matchingMainOption.subField) {
                    const subFieldOptionsResponse =
                        matchingMainOption.subField.fieldOptions || [];
                    const subDraft = subQuestionDrafts[option.id];

                    subDraft.fieldOptions?.forEach((subOption, subIndex) => {
                        const matchingSubOption = subFieldOptionsResponse.find(
                            (subResOpt) => subResOpt.optionOrder === subIndex + 1
                        );

                        if (matchingSubOption) {
                            if (
                                subOption.image &&
                                (subOption.image.name || subOption.image instanceof File)
                            ) {
                                // console.log(`Adding sub-option ${subIndex + 1} image to upload queue`);
                                imageUploads.push({
                                    optionId: matchingSubOption.id,
                                    file: subOption.image,
                                    isFinishing: false,
                                    type: "sub",
                                    description: `Sub-option ${subIndex + 1} image`,
                                });
                            }

                            if (
                                subOption.finishingImage &&
                                (subOption.finishingImage.name ||
                                    subOption.finishingImage instanceof File)
                            ) {
                                // console.log(`Adding sub-option ${subIndex + 1} finishing image to upload queue`);
                                imageUploads.push({
                                    optionId: matchingSubOption.id,
                                    file: subOption.finishingImage,
                                    isFinishing: true,
                                    type: "sub",
                                    description: `Sub-option ${subIndex + 1} finishing image`,
                                });
                            }
                        }
                    });
                }
            }
        });

        return imageUploads;
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            toast.error("Please fill all required fields");
            return;
        }

        setIsLoading(true);

        try {
            if (editAbleQuestionObj?.id) {
                await handleUpdate();
            } else {
                const payload = buildPayload();
                const resp = await createFormFieldWithSub(payload);

                if (resp?.success) {
                    const imageUploads = buildImageUploadMap(resp.data);

                    if (imageUploads.length > 0) {
                        console.log(
                            "Starting image uploads:",
                            imageUploads.map((u) => ({
                                type: u.type,
                                description: u.description || `${u.type} option image`,
                                isFinishing: u.isFinishing,
                                fileName: u.file?.name,
                            }))
                        );

                        toast.loading(`Uploading ${imageUploads.length} image(s)...`);

                        for (var i = 0; i < imageUploads.length; i++) {
                            try {
                                await updateFormFieldOptionImage(
                                    imageUploads[i].optionId,
                                    imageUploads[i].file,
                                    imageUploads[i].isFinishing
                                );
                            } catch (error) {
                                console.error(`Error fetching from ${imageUploads[i]}:`, error);
                                // Handle the error appropriately, e.g., break the loop or continue
                            }
                        }
                        toast.dismiss();
                    } else {
                        toast.success("Question created successfully");
                    }

                    refetchQuestionsList();
                    handleClose();
                } else {
                    toast.error(resp?.error || "Create failed");
                }
            }
        } catch (e) {
            console.error(e);
            toast.error("Save failed");
        }

        setIsLoading(false);
    };

    const handleUpdate = async () => {
        const storedUserInfo = localStorage.getItem("userInfo") ?? "{}";
        const parsedUserInfo = JSON.parse(storedUserInfo);

        const originalIds =
            editAbleQuestionObj.fieldOptions?.map((opt) => opt.id) || [];
        const currentIds = options
            .map((opt) => opt.id)
            .filter((id) => !id.toString().startsWith("new-"));
        const removedIds = originalIds.filter((id) => !currentIds.includes(id));

        // console.log("Original IDs:", originalIds);
        // console.log("Current IDs:", currentIds);
        // console.log("Removed IDs:", removedIds);

        const preparedOptions = options
            .filter((opt) => opt.text && opt.text.trim())
            .map((option, index) => {
                const optionData = {
                    fieldOption: option.text.trim(),
                    optionOrder: index + 1,
                };

                if (formType === "single-question" && option.bgColorCode) {
                    optionData.bgColorCode = option.bgColorCode;
                }

                if (option.finishingDesc && option.finishingDesc.trim()) {
                    optionData.finishingDesc = option.finishingDesc.trim();
                }

                optionData.customAction = option.customAction;

                if (option.id.toString().startsWith("new-")) {
                    return optionData;
                } else {
                    return { id: option.id, ...optionData };
                }
            });

        const payload = {
            id: editAbleQuestionObj.id,
            formId: formId,
            fieldName: question?.trim(),
            fieldType: questionType,
            userID: parsedUserInfo?.userId,
            mandatory: isMandatory,
            removeFieldOptionIds: removedIds,
        };

        if (questionType !== FREE_QUE_TYPE.id) {
            payload.fieldOptions = preparedOptions;
        }

        const response = await updateFormField(payload);

        if (response?.success) {
            await handleImageUploads(response.data);
            toast.success("Question updated successfully");
            refetchQuestionsList();
            handleClose();
        } else {
            toast.error(response?.error || "Failed to update question");
        }
    };

    const handleImageUploads = async (responseData) => {
        const fieldOptionsResponse = responseData?.fieldOptions || [];

        const optionImagePromises = options
            .map((opt, index) => {
                if (opt.image && opt.image.name) {
                    const matchingOption = fieldOptionsResponse.find(
                        (resOpt) => resOpt.optionOrder === index + 1
                    );
                    if (matchingOption) {
                        return updateFormFieldOptionImage(
                            matchingOption.id,
                            opt.image,
                            false
                        );
                    }
                }
                return null;
            })
            .filter(Boolean);

        const finishingImagePromises = options
            .map((opt, index) => {
                if ((opt.finishingImage && opt.finishingImage.name) || opt.finishingImage === "remove") {
                    const matchingOption = fieldOptionsResponse.find(
                        (resOpt) => resOpt.optionOrder === index + 1
                    );
                    if (matchingOption) {

                        if (opt.finishingImage === "remove") {
                            return RemoveFormFieldOptionImage(
                                matchingOption.id,
                                true
                            );
                        }
                        else {
                            return updateFormFieldOptionImage(
                                matchingOption.id,
                                opt.finishingImage,
                                true
                            );
                        }
                    }
                }
                return null;
            })
            .filter(Boolean);

        const allPromises = [...optionImagePromises, ...finishingImagePromises];
        if (allPromises.length > 0) {
            await Promise.all(allPromises);
        }
    };

    // 删除操作
    const handleDelete = async () => {
        if (!editAbleQuestionObj?.id) return;

        try {
            const response = await deleteFormField(editAbleQuestionObj.id);
            if (response?.success) {
                toast.success("Question deleted successfully");
                refetchQuestionsList();
                handleClose();
            } else {
                toast.error(response?.error || "Failed to delete question");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            toast.error("Failed to delete question");
        }
        setIsDeleteFormModal(false);
    };

    const handleClose = () => {
        setisPreivewEnabled(false);
        setQuestion("");
        if (formType === "checklist") {
            setQuestionType(SINGLE_QUE_TYPE.id);
        } else if (formType === "single-question") {
            setQuestionType(SINGLE_QUE_TYPE.id);
        } else {
            setQuestionType(MULTIPLE_QUE_TYPE.id);
        }
        setEditAbleQuestionObj(null);
        setOptions(createDefaultOptions());
        onClose();
    };

    const [isPreivewEnabled, setisPreivewEnabled] = useState(false);
    const getModalTitle = () => {
        if (editAbleQuestionObj?.id) {
            return formType === "checklist" ? "Edit question" : "Edit question";
        } else {
            switch (formType) {
                case "checklist":
                    return question ? "Add question" : "New question";
                case "single-question":
                    return question ? "Add question" : "New question";
                default:
                    return question ? "Add question" : "New question";
            }
        }
    };

    const mandatoryToggleHandler = (e) => {
        setIsMandatory(e.target.checked);
    };

    const onDeletePressHandler = () => {
        setIsDeleteFormModal(true);
    };

    const onCancelHandler = () => {
        handleClose();
    };

    const onCloseDeleteModal = () => {
        setIsDeleteFormModal(false);
    };

    const shouldShowAddButton = () => {
        if (formType === "single-question") {
            return options.length < 4;
        } else if (formType === "checklist") {
            return options.length < 3;
        } else {
            return options.length < 6;
        }
    };

    if (!isVisible) return null;

    const availableQuestionTypes = getAvailableQuestionTypes();
    const saveButtonEnabled = isFormValid();
    const canDuplicate = true;

    const handleSubQuestionDraftSave = (optionId, draft) => {
        // console.log("Saving sub-question draft with images:", draft);

        setSubQuestionDrafts((prev) => ({ ...prev, [optionId]: draft }));
    };

    return (
        <>
            <div className="modal" tabIndex="-1" role="dialog" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", }} >
                <div className="modal-dialog" role="document" style={{
                    display: "inline-block", alignItems: "center", justifyContent: "center", minHeight: "100vh",
                    margin: "0 auto", maxWidth: "100%", width: "auto"
                }} >
                    <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column", maxWidth: "fit-content" }} >
                        <div className="modal-header">
                            <h1 className="modal-title">{getModalTitle()}</h1>
                            <button className="close-button" disabled={isLoading} onClick={handleClose} >
                                &times;
                            </button>
                        </div>

                        <div className="modal-body" style={{ flex: 1, padding: "20px" }} >
                            <table border="0" width="100%">
                                <tr>
                                    <td style={{ width: isPreivewEnabled ? "49%" : "100%", border: "0", verticalAlign: "top" }}>
                                        <label className="input-label">
                                            {formType === "checklist" ? "Checklist Item" : "Question Title"}
                                        </label>
                                        <input className="question-title-input" type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
                                            placeholder={
                                                formType === "checklist"
                                                    ? "Untitled checklist item"
                                                    : "Untitled question"
                                            }
                                        />

                                        <div
                                            style={{ gap: 20 }}
                                            className="d-flex align-items-center mt-3 gap-2"
                                        >
                                            <CustomSelect
                                                questionTypesOptions={availableQuestionTypes}
                                                questionType={questionType}
                                                setQuestionType={(value) => {
                                                    setQuestionType(value);
                                                }}
                                                disabled={
                                                    formType === "checklist" || formType === "single-question"
                                                }
                                            />

                                            <div className="form-check form-check-inline">
                                                <input
                                                    style={{ width: "20px", height: "20px" }}
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="termsCheck"
                                                    onChange={mandatoryToggleHandler}
                                                    checked={isMandatory}
                                                    disabled={
                                                        formType === "single-question" || formType === "checklist"
                                                    }
                                                />
                                                <label
                                                    className="form-check-label ml-1"
                                                    htmlFor="termsCheck"
                                                    style={{
                                                        opacity:
                                                            formType === "single-question" || formType === "checklist"
                                                                ? 0.6
                                                                : 1,
                                                        cursor:
                                                            formType === "single-question" || formType === "checklist"
                                                                ? "not-allowed"
                                                                : "pointer",
                                                    }}
                                                >
                                                    Mandatory
                                                </label>
                                            </div>
                                        </div>

                                        {formType === "checklist" && (
                                            <div
                                                style={{
                                                    marginTop: "10px",
                                                    padding: "10px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "4px",
                                                    fontSize: "14px",
                                                    color: "#6c757d",
                                                }}
                                            >
                                                Note: Checklist items are automatically set to single choice type.
                                            </div>
                                        )}

                                        {formType === "single-question" && (
                                            <div
                                                style={{
                                                    marginTop: "10px",
                                                    padding: "10px",
                                                    backgroundColor: "#fff3cd",
                                                    borderRadius: "4px",
                                                    fontSize: "14px",
                                                    color: "#856404",
                                                }}
                                            >
                                                Note: Single-question forms are automatically set to single choice
                                                type,<br /> require 2-4 options, and only one question is allowed.
                                            </div>
                                        )}

                                        {questionType !== FREE_QUE_TYPE.id && (
                                            <div style={{ marginTop: "20px" }}>
                                                <DndContext
                                                    sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <SortableContext
                                                        items={options.map((opt) => opt.id)}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        <div className="options-container">
                                                            {options.map((option, index) => (
                                                                <SortableOptionItem
                                                                    key={option.id}
                                                                    option={option}
                                                                    index={index}
                                                                    questionType={questionType}
                                                                    formType={formType}
                                                                    showRemoveButton={options.length > 2}
                                                                    onInputChange={handleOptionInputChange}
                                                                    onImageChange={handleOptionImageChange}
                                                                    onColorChange={handleOptionColorChange}
                                                                    onRemove={handleRemoveOption}
                                                                    onAddSubQuestion={handleAddSubQuestion}
                                                                    onEditSubQuestion={handleEditSubQuestion}
                                                                    onAddFinishingPage={handleAddFinishingPage}
                                                                    onEditFinishingPage={handleEditFinishingPage}
                                                                    onDuplicate={handleDuplicateOption}
                                                                    canDuplicate={canDuplicate}
                                                                    subQuestionList={subQuestionList}
                                                                    subQuestionDrafts={subQuestionDrafts}
                                                                    onAddEmailAction={handleAddEmailAction}
                                                                />
                                                            ))}
                                                        </div>
                                                    </SortableContext>
                                                </DndContext>

                                                {shouldShowAddButton() && (
                                                    <button onClick={handleAddOption} className="add-option-btn">
                                                        + Add option
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    {isPreivewEnabled &&
                                        <>
                                            <td style={{ width: "0.5%", border: "0" }}>
                                                &nbsp;
                                            </td>
                                            <td style={{ width: "0.5%", borderLeft: "1px solid #ccc", borderBottom: "0px solid #ccc" }}>
                                            </td>
                                            <td style={{ width: "49%", border: "0" }}>
                                                <div style={{ width: "100%" }} >
                                                    <PreviewQuestionDetails formType={formType}
                                                        Question={{
                                                            fieldName: question, fieldType: questionType,
                                                            fieldOptions: options.filter(a => a.text !== "").map(item => {
                                                                return {
                                                                    ...item, // Spreading existing properties
                                                                    fieldOption: item.text,
                                                                    imageURL: item.image != null && item.image?.name === undefined && item.image.includes("https") ? item.image : null
                                                                };
                                                            })
                                                        }} />
                                                </div>
                                            </td>
                                        </>
                                    }
                                </tr>
                            </table>
                            {/* Modal Actions */}
                            <div className="modal-actions">
                                {editAbleQuestionObj?.id ? (
                                    <button
                                        className="delete-btn btn btn-outline-danger"
                                        disabled={isLoading}
                                        onClick={onDeletePressHandler}
                                    >
                                        Delete
                                    </button>
                                ) : (
                                    <></>
                                )}
                                <button
                                    className="cancel-btn"
                                    disabled={isLoading}
                                    onClick={onCancelHandler}
                                >
                                    Cancel
                                </button>
                                {isLoading ? (
                                    <Loader style={{ marginLeft: 20 }} />
                                ) : (
                                    <button
                                        className="save-btn"
                                        onClick={handleSave}
                                        disabled={!saveButtonEnabled}
                                        style={{
                                            cursor: saveButtonEnabled ? "pointer" : "not-allowed",
                                            opacity: saveButtonEnabled ? 1 : 0.6,
                                            marginRight: "8px"
                                        }}
                                    >
                                        Save
                                    </button>
                                )}

                                <button className="save-btn" onClick={(e) => { e.preventDefault(); setisPreivewEnabled(!isPreivewEnabled) }}>
                                    {isPreivewEnabled ? "Hide Preview <" : "Show Preview >"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <DeleteModal
                visible={isDeleteFormModal}
                onClose={onCloseDeleteModal}
                onCancel={onCloseDeleteModal}
                onDelete={handleDelete}
                title={`Are you sure you want to delete this ${formType === "checklist" ? "checklist item" : "question"
                    } ?`}
            />

            <AddEditSubQuestionModal
                editAbleQuestionObj={editAbleQuestionObj}
                isVisible={isSubQuestionModalVisible}
                SelectedOptionId={selectedOptionForSubQuestion}
                onClose={handleSubQuestionClose}
                refetchQuestionsList={refetchQuestionsList}
                formType={formType}
                subQuestionList={subQuestionList}
                formId={formId}
                standalone={subModalStandalone}
                onDraftSave={handleSubQuestionDraftSave}
                subQuestionDrafts={subQuestionDrafts}
                customEmailText={customEmailText}
            />

            <AddEditFinishingPageModal
                isVisible={isFinishingPageModalVisible}
                selectedOption={selectedOptionForFinishingPage}
                onClose={handleFinishingPageClose}
                onSave={handleFinishingPageSave}
            />

            <AddEditEmailAction visible={isEmailActionVisible} onClose={handleCloseEmailAction} onCancel={handleCloseEmailAction} selectedOption={selectedOptionForEmailAction}
                onEmailSubmitAction={onEmailSubmitAction} customEmailText={customEmailText} questionText={question} />
        </>
    );
};

export default memo(AddEditQuestionModal);
